import {Box, Button, Grid} from "@mui/material";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
} from "@mui/x-data-grid";
import * as React from "react";
import { useMemo, useState } from "react";
import ReactTableStyles from "./ReactTableStyles";
import MultiFilterPanel from "../ServerPaginatedTable/MultiFilterPanel";
import { applyClientFilter } from "./applyClientFilter";
import ExcelExportButton from "../ExcelExport/ExcelExportButton";
/**
 * @param excelExport optional props for the server-side Excel export button
 *                    ({ endpoint, filters, sortBy, sortDir, fileNamePrefix, storageKey }).
 *                    Omitted by every listing that has not opted in, in which case no button
 *                    renders and the toolbar is unchanged.
 */
export const CustomColumnToolbar = (column, filters, density, exports, csvFileName, search, excelExport) => {
  return (
    <ReactTableStyles>
      <Grid container justifyContent="space-between">
        {((filters || column || density || exports || excelExport) && (
          <Grid item>
            <Box className="filtersWrapper">
              {filters && (
                <Box className="toolbarLeftObject">
                  <GridToolbarFilterButton className="toolbarButton" />
                </Box>
              )}
              {column && (
                <Box className="toolbarLeftObject">
                  <GridToolbarColumnsButton className="toolbarButton" />
                </Box>
              )}
              {density && (
                <Box className="toolbarLeftObject">
                  <GridToolbarDensitySelector className="toolbarButton" />
                </Box>
              )}
              {exports && (
                <Box className="toolbarLeftObject">
                  <GridToolbarExport className="toolbarButton" csvOptions={{fileName:csvFileName}} printOptions={{ disableToolbarButton: true }}/>
                </Box>
              )}
              {excelExport && <ExcelExportButton {...excelExport} />}
            </Box>
          </Grid>
        )) || <Grid item></Grid>}
        {search && (
          <Grid item>
            <Box className="toolbarRightObject">
              <GridToolbarQuickFilter className="toolbarButton" />
            </Box>
          </Grid>
        )}
      </Grid>
    </ReactTableStyles>
  );
};

/**
 * Toolbar for the client-side grid.
 *
 * Declared at module scope rather than inline in `slots` so its identity is stable across renders;
 * an inline arrow gets a new identity each render, which remounts the toolbar and discards any
 * state it holds — such as the export dialog's open flag.
 */
function ClientTableToolbar({
  column,
  filters,
  density,
  exports,
  exportedFileName,
  search,
  excelExport,
}) {
  return CustomColumnToolbar(column, filters, density, exports, exportedFileName, search, excelExport);
}

export default function ReactTable({
  data,
  columns,
  checkboxSelection,
  disableRowSelectionOnClick,
  columnsEnable,
  filtersEnable,
  densityEnable,
  exportEnable,
  exportedFileName,
  searchBarEnable,
  pageSizeOptions,
  InitialPageSize,
  disableColumnMenu, initialColumnVisibilityModel,
  height="70vh",
  id,
  getRowClassName,
  className,
  isLoading = false,
  excelExport = null,
}) {
  const column = !!columnsEnable;
  const filters = !!filtersEnable;
  const density = !!densityEnable;
  const exports = !!exportEnable;
  const search = !!searchBarEnable;
  const sizeOptions = [5,10, 25, 50, 100];
  const pageSize =   10;

  // ---- Client-side multi-filter state ----
  const [filterModel, setFilterModel] = useState({ items: [], logicOperator: "and" });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const apiRef = useGridApiRef();

  // Build a map of field → valueGetter for virtual columns so filters use resolved values.
  const valueGetterMap = useMemo(() => {
    const map = {};
    (columns || []).forEach((col) => {
      if (col.field && typeof col.valueGetter === "function") {
        map[col.field] = col.valueGetter;
      }
    });
    return map;
  }, [columns]);

  const filteredData = useMemo(() => {
    const items = filterModel?.items || [];
    if (!items.length) return data || [];
    return (data || []).filter((row) =>
      items.every((item) => {
        // If a valueGetter exists for this field, resolve the value first.
        const getter = valueGetterMap[item.field];
        const resolvedRow = getter
          ? { ...row, [item.field]: getter({ row }) }
          : row;
        return applyClientFilter(resolvedRow, item);
      })
    );
  }, [data, filterModel, valueGetterMap]);

  const applyClientFilters = (model) => {
    setFilterModel(model || { items: [], logicOperator: "and" });
  };

  React.useEffect(() => {
    if (!apiRef.current) return;
    const unsubscribe = apiRef.current.subscribeEvent("stateChange", () => {
      const preferencePanelState = gridPreferencePanelStateSelector(apiRef.current.state);
      setIsFilterPanelOpen(
        preferencePanelState.open &&
          preferencePanelState.openedPanelValue === GridPreferencePanelsValue.filters
      );
    });
    return () => unsubscribe();
  }, [apiRef]);

  return (
    <ReactTableStyles tableHeight={height}>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <DataGrid
              apiRef={apiRef}
            getRowId={id && ((row) => row[id])}
            className={`${className ? className : ''} tableHeight `}
            getRowClassName={(params) =>
              {
                let className = params.indexRelativeToCurrentPage % 2 === 0
                ? "Mui-even"
                : "Mui-odd";
                className += getRowClassName?.(params)??'';
                return className;
              }
            }
            classes={{
              root: "overrideRoot",
              cell: "overrideCell",
              columnHeader: "columnHeaderOverride",
              row: "overrideRow",
              virtualScroller: 'overrideVirtualScroller'
            }}
            columns={columns}
            disableColumnMenu={disableColumnMenu}
            rows={filteredData}
            loading={!!isLoading}
            slots={{
              toolbar: ClientTableToolbar,
              filterPanel: MultiFilterPanel,
            }}
            slotProps={{
              toolbar: {
                column,
                filters,
                density,
                exports,
                exportedFileName,
                search,
                // filteredData is what the grid is actually showing, so a client-side export
                // reflects the filters the user applied here.
                excelExport: excelExport
                  ? { ...excelExport, visibleRows: filteredData }
                  : null,
              },
              filterPanel: {
                columns,
                apiRef,
                applyFiltersCallback: applyClientFilters,
                onClose: () => apiRef.current?.hideFilterPanel?.(),
              },
            }}
            filterModel={filterModel}
            filterMode="server"
            onFilterModelChange={(newModel) => setFilterModel(newModel)}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            initialState={{
              pagination: {
                paginationModel: { pageSize: pageSize, page: 0 },
              },
              columns: {
                columnVisibilityModel: initialColumnVisibilityModel,
              },
            }}
            pageSizeOptions={sizeOptions}
          />
        </Grid>
      </Grid>
    </ReactTableStyles>
  );
}